import React, { useState, useEffect, useContext } from 'react';
import { IServiceSpec } from 'data/service-spec';
import { useToggle, camelToWords, slashTrim, useAsyncCache, removeSuffix, StateTreeSetter } from 'utility';
import _ from 'lodash';
import { config } from 'config';
import { ISummary } from 'data/summary';
import { useHistory, Link } from 'react-router-dom';
import { DisplayContext } from './editor-page';

interface IDirectoryBlockProps {
    path: string;
    selections: any;
    setSelection: StateTreeSetter<object>;
    service: IServiceSpec;
}

interface IDirectoryWrapperProps extends IDirectoryBlockProps {
    invalidator: () => void;
}

const DirectoryBlockWrapper = ({ path, selections, setSelection, service, invalidator }: IDirectoryWrapperProps) => {
    const [ isOpen, toggleOpen ] = useToggle(!_.isEmpty(selections));
    const history = useHistory();

    const pathParts = slashTrim(path).split('/');
    const title = camelToWords(_.last(pathParts) || '');
    const onSelectDirectory = () => {
        //setSelection(ensureSingleSelection(dirPath));
        history.push('/' + path + '/');
    };
    const onDelete = (path: string[]) => async () => {
        const dirPath = path.join('/') + '/';
        if (!window.confirm(`Sure you want to delete ${dirPath}?`)) return;
        const status = await config.api.deleteItem(dirPath);
        if (status === 400) {
            alert('Please ensure the directory is empty before deleting');
            return;
        } else if (status) {
            alert('There was an error');
            return;
        }
        invalidator();
        history.replace('/' + path.slice(0, -1).join('/') + '/?deleted');
    };

    return (
    <li className="content-type">
        <h2 className={selectedClass('', selections)}>
            <span className={isOpen ? "jd-toggle is-open" : "jd-toggle is-closed"} onClick={toggleOpen}></span>
            <span
                className="jd-directory"
                onClick={onSelectDirectory}>
                {title}
            </span>
            <span className="sf-control-button sf-delete-button" onClick={onDelete(pathParts)}>x</span>
        </h2>
        {isOpen && <DirectoryBlock {...{ selections, setSelection, path, service }} />}
    </li>
    )
}

const selectedClass = (name: string, selections: any) =>
    (selections && (selections[name] || selections[removeSuffix(name)] || (name === '' && selections === true)) ? "jd-selected" : "");

export const DirectoryBlock = ({ path, selections, setSelection, service }: IDirectoryBlockProps) => {
    const [ dir, invalidateDir ] = useAsyncCache(null, (uid) => config.api.fetchList(path, uid));
    const [ additionalFiles, setAdditionalFiles ] = useState<ISummary[]>([]);
    const [ newDirName, setNewDirName ] = useState<string | null>(null);
    const history = useHistory();
    const [ , setDisplay ] = useContext(DisplayContext);
    const files = (dir && dir.files.concat(dir.dirs || [])) || [];

    // reload the dir if selections contains an item not listed in dir.files
    useEffect(() => {
        if (!selections || !dir) return; // skip if dir not yet loaded
        const mime = (dir && dir.mimeTypes[0]) || '';
        const defaultExt = (mime && ('.' + config.mime.getExtension(mime))) || '';
        const allFiles = files.concat(additionalFiles);
        const selectionNotInDir = Object.keys(selections).find(key => !allFiles.find(file => file.name === key || file.name === key + '/' || file.name === key + defaultExt));
        if (selectionNotInDir) invalidateDir();
    }, [ selections ]);

    const propPath = path.split('/').filter(pathEl => !!pathEl);
    const invalidator = () => {
        invalidateDir();
    }

    const onSelect = (path: string[]) => () => {
        //if (!dir) return;

        //setSelection(ensureSingleSelection(path));
        if (_.last(path) === '') {
            path.pop();
            let newUrl = '/' + path.join('/') + "/?new";
            newUrl = newUrl.replace('//', '/');
            if (dir && dir.mimeTypes) {
                newUrl += '&extensions=' + dir.mimeTypes
                    .map(mime => config.mime.getExtension(mime))
                    .filter(ext => !!ext)
                    .join(',');
            } 
            history.push(newUrl);
        } else {
            history.push(('/' + path.join('/')).replace('//', '/'));
        }
    }

    const onLinkClick = () => {
        if (window.innerWidth <= 500) {
            setDisplay({ showPane: true });
        }
    }

    const urlFromPath = (path: string[]) => {
        if (_.last(path) === '') {
            path.pop();
            let newUrl = '/' + path.join('/') + "/?new";
            newUrl = newUrl.replace('//', '/');
            if (dir && dir.mimeTypes) {
                newUrl += '&extensions=' + dir.mimeTypes
                    .map(mime => config.mime.getExtension(mime))
                    .filter(ext => !!ext)
                    .join(',');
            } 
            return newUrl;
        } else {
            return ('/' + path.join('/')).replace('//', '/');
        }
    }

    const onDelete = (path: string[]) => async () => {
        if (!window.confirm("Sure you want to delete?")) return;
        await config.api.deleteItem(path.join('/'));
        invalidator();
        history.replace('/' + path.slice(0, -1).join('/') + '/?deleted');
    };

    const onAddDir = () => setNewDirName('');
    const onChangeDirName = (ev: React.FormEvent) => setNewDirName(ev.target['value']);
    const onCreateDir = () => {
        if (newDirName) {
            setAdditionalFiles(files => [...files, { name: newDirName + '/' }]);
        }
        setNewDirName(null);
    }
    const onKeyUp = (ev: React.KeyboardEvent) => {
        if (ev.key === 'Enter') onCreateDir();
    }

    let filterTest = (f: ISummary) => true;
    if (service.apisSupported.includes('jsonData')) filterTest = f => !f.name.endsWith('.config.json');
    let showFiles = _.sortBy(
        _.uniqBy(
            _.concat((dir && dir.files.filter(filterTest)) || [],
                (dir && dir.dirs) || [], 
                additionalFiles),
            file => file.name),
        file => file.name
    );

    return (<ul>
        <li className="jd-add-controls" key='_add'>
            <span className="sf-control-button sf-add-button" onClick={onSelect([ ...propPath, '' ])}>+</span>
            <span className="sf-control-button sf-add-button jd-add-dir-button" onClick={onAddDir}>/</span>
        </li>
        {showFiles.map(f => f.name).map((filename) => (
            filename.endsWith('/') 
            ? (<DirectoryBlockWrapper
                selections={selections && selections[slashTrim(filename)]}
                setSelection={setSelection}
                key={filename}
                path={path + '/' + slashTrim(filename)}
                service={service}
                invalidator={invalidator}
                />)
            : (<li key={filename}
                className={selectedClass(filename, selections)}>
                    <Link to={urlFromPath([ ...propPath, filename ])} onClick={onLinkClick} className="jd-select-item">{removeSuffix(filename)}</Link>
                    <a className="sf-control-button jd-live-button"
                        href={config.api.urlFromPath(urlFromPath([ ...propPath, filename ]))}
                        target="_blank">
                    </a>
                    <span className="sf-control-button sf-delete-button" onClick={onDelete([ ...propPath, filename ])}>x</span>
                </li>)
        ))}
        {newDirName !== null &&
            <li key="_dir" className="content-type">
                <span className="jd-toggle is-closed"></span>
                <input autoFocus className="jd-dir-input"
                    value={newDirName}
                    onChange={onChangeDirName}
                    onBlur={onCreateDir}
                    onKeyUp={onKeyUp}></input>
            </li>}
    </ul>);
}